import React from "react"
import { useTranslation } from "react-i18next"

import { DnDProvider } from "shared/context/drag-and-drop/DnDProvider"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { Toggle } from "shared/ui/input/boolean/toggle/Toggle"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import { ProductPositionTable } from "widgets/product-position-table/ProductPositionTable"
import { ProductPositionTableHeader } from "widgets/product-position-table/ProductPositionTableHeader"

import { MusterheftExportTabBar } from "./MusterheftExportTabBar"
import { MusterheftExportOverlayViewModel } from "./model/useMusterheftExportOverlayViewModel"
import "./styles/_musterheft-export-overlay.scss"

export const MusterheftExportOverlayView = React.memo(
  ({
    projectID,
    isLoading,
    floors,
    unassignedRoomsCount,
    unassignedProductsCount,
    visibleChildren,
    selectedItems,
    onSelectionChange,
    activeTabID,
    onSelectTab,
    exportAll,
    onToggleExportAll,
    isExportDisabled,
    onSubmit,
    onCancel,
    RowRenderer,
    columnPreset,
    isItemCollapsed,
    onItemCollapse,
    onCollapseAll,
    onExpandAll,
    isAllSelected,
    isSomeSelected,
    onSelectAll
  }: MusterheftExportOverlayViewModel) => {
    const { t } = useTranslation()

    return (
      <DnDProvider>
        <FormOverlay
          id="musterheft-export-overlay"
          form="musterheft-export-form"
          prefix="musterheft-export"
          className="musterheft-export-overlay"
          headline={t("MusterheftExport.Headline")}
          onCancel={onCancel}
          submitButtonText={t("MusterheftExport.SubmitButton")}
          isSubmitDisabled={isExportDisabled}
          showCloseButton
        >
          {/* Both submit buttons FormOverlay renders (tablet header, desktop
              footer) are wired to this form by id, so the export has to run on
              the form's submit event rather than on a button click. */}
          <form
            id="musterheft-export-form"
            className="musterheft-export-overlay__content"
            onSubmit={onSubmit}
          >
            <div className="musterheft-export-overlay__intro">
              {t("MusterheftExport.IntroText")}
            </div>

            <div className="musterheft-export-overlay__contents-section">
              <h3 className="musterheft-export-overlay__contents-header">
                {t("MusterheftExport.Contents.Title")}
              </h3>
              <ul className="musterheft-export-overlay__contents">
                <li>{t("MusterheftExport.Contents.Rendering")}</li>
                <li>{t("MusterheftExport.Contents.CategoryAndType")}</li>
                <li>{t("MusterheftExport.Contents.PositionRemarks")}</li>
                <li>{t("MusterheftExport.Contents.GeneralInfo")}</li>
              </ul>
            </div>

            <Toggle.Root className="musterheft-export-overlay__toggle toggle--horizontal">
              <div className="toggle-control toggle-control--horizontal">
                <Toggle.Label>{t("MusterheftExport.ExportAll")}</Toggle.Label>
                <Toggle.Input
                  checked={exportAll}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onToggleExportAll(event.target.checked)
                  }
                />
              </div>
            </Toggle.Root>

            {exportAll ? null : (
              <Hint.Root className="musterheft-export-overlay__hint hint--banner">
                <Hint.Icon name={IconName.INFO} />
                <Hint.Title>{t("MusterheftExport.SelectionHint")}</Hint.Title>
              </Hint.Root>
            )}

            {exportAll ? null : (
              <div className="musterheft-export-overlay__tab-bar-container">
                <MusterheftExportTabBar
                  activeKey={activeTabID ?? ""}
                  onSelect={onSelectTab}
                  floors={floors}
                  unassignedRoomsCount={unassignedRoomsCount}
                  unassignedProductsCount={unassignedProductsCount}
                />
              </div>
            )}

            {exportAll ? null : (
              <div className="card card--elevated musterheft-export-overlay__table-container">
                <div className="musterheft-export-overlay__table-header">
                  <ProductPositionTableHeader
                    columnPreset={columnPreset}
                    showSelectionCheckbox
                    isAllSelected={isAllSelected}
                    isSomeSelected={isSomeSelected}
                    onSelectAll={onSelectAll}
                    onCollapseAll={onCollapseAll}
                    onExpandAll={onExpandAll}
                  />
                </div>
                <ProductPositionTable
                  projectID={projectID}
                  activeTabID={activeTabID}
                  projectChildren={visibleChildren}
                  isLoading={isLoading}
                  selectedItems={selectedItems}
                  onSelectionChange={onSelectionChange}
                  columnPreset={columnPreset}
                  isItemCollapsed={isItemCollapsed}
                  onItemCollapse={onItemCollapse}
                  additiveSelect
                  rowFocusOnly
                  showSelectionCheckbox
                  isSortingDisabled
                  RowRenderer={RowRenderer}
                />
              </div>
            )}
          </form>
        </FormOverlay>
      </DnDProvider>
    )
  }
)
